// Live wallet state — balance, free trial seconds, subscription, and helpers
// for top-up, portal, and the 30-day subscription nudge.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface WalletState {
  balance_cents: number;
  free_seconds_remaining: number;
  auto_refill_enabled: boolean;
  stripe_customer_id: string | null;
  first_paid_at: string | null;
  nudge_dismissed_until: string | null;
}

export interface SubscriptionState {
  tier_id: string | null;
  status: string;
  current_period_end: string | null;
  monthly_credits_remaining_cents: number;
  cancel_at_period_end: boolean;
}

export interface TxRow {
  id: string;
  kind: string;
  amount_cents: number;
  description: string | null;
  created_at: string;
}

const NUDGE_THRESHOLD_CENTS = 3000;

export function useWallet() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState<WalletState | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [recentTx, setRecentTx] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setWallet(null);
      setSubscription(null);
      setLoading(false);
      return;
    }
    const [{ data: w }, { data: s }, { data: tx }] = await Promise.all([
      supabase.from("user_wallet").select("*").eq("user_id", user.id).maybeSingle(),
      supabase.from("user_subscriptions").select("*").eq("user_id", user.id).maybeSingle(),
      supabase
        .from("wallet_transactions")
        .select("id,kind,amount_cents,description,created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    setWallet(w as WalletState | null);
    setSubscription(s as SubscriptionState | null);
    setRecentTx((tx as TxRow[] | null) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: refresh on any wallet/tx/subscription change for this user.
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`wallet:${user.id}:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_wallet", filter: `user_id=eq.${user.id}` },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "wallet_transactions", filter: `user_id=eq.${user.id}` },
        () => refresh(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_subscriptions", filter: `user_id=eq.${user.id}` },
        () => refresh(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, refresh]);

  const isTrialing = useMemo(() => {
    if (!wallet) return false;
    return !wallet.first_paid_at && wallet.free_seconds_remaining > 0;
  }, [wallet]);

  const hasFunds = useMemo(() => {
    if (!wallet) return false;
    if (subscription?.status === "active" && subscription.monthly_credits_remaining_cents > 0) return true;
    if (wallet.balance_cents > 0) return true;
    if (isTrialing) return true;
    return false;
  }, [wallet, subscription, isTrialing]);

  // Track usage heartbeat. Call start() when streaming begins; stop() when done.
  const heartbeatRef = useRef<{ timer: number | null; lastTick: number }>({ timer: null, lastTick: 0 });
  const startUsage = useCallback(() => {
    if (!user) return;
    if (heartbeatRef.current.timer) return;
    heartbeatRef.current.lastTick = Date.now();
    const tick = async () => {
      const now = Date.now();
      const delta = Math.round((now - heartbeatRef.current.lastTick) / 1000);
      heartbeatRef.current.lastTick = now;
      if (delta > 0 && isTrialing) {
        try {
          await supabase.functions.invoke("wallet-track-usage", { body: { seconds: delta } });
        } catch { /* ignore */ }
      }
    };
    heartbeatRef.current.timer = window.setInterval(tick, 15000);
  }, [user, isTrialing]);

  const stopUsage = useCallback(async () => {
    if (heartbeatRef.current.timer) {
      window.clearInterval(heartbeatRef.current.timer);
      heartbeatRef.current.timer = null;
    }
    const delta = Math.round((Date.now() - heartbeatRef.current.lastTick) / 1000);
    if (delta > 0 && user && isTrialing) {
      try {
        await supabase.functions.invoke("wallet-track-usage", { body: { seconds: delta } });
      } catch { /* ignore */ }
    }
  }, [user, isTrialing]);

  // 30-day subscription nudge: shown once if trailing-30d topups > threshold
  // and not subscribed and not dismissed recently.
  const shouldShowNudge = useMemo(() => {
    if (!wallet || !wallet.first_paid_at) return false;
    if (subscription?.status === "active") return false;
    const ageDays = (Date.now() - new Date(wallet.first_paid_at).getTime()) / 86400000;
    if (ageDays < 30) return false;
    if (wallet.nudge_dismissed_until && new Date(wallet.nudge_dismissed_until) > new Date()) return false;
    const cutoff = Date.now() - 30 * 86400000;
    const recentTopups = recentTx
      .filter((t) => t.kind === "topup" && new Date(t.created_at).getTime() > cutoff)
      .reduce((sum, t) => sum + t.amount_cents, 0);
    return recentTopups >= NUDGE_THRESHOLD_CENTS;
  }, [wallet, subscription, recentTx]);

  const recommendedTier = useMemo(() => {
    const cutoff = Date.now() - 30 * 86400000;
    const spend = recentTx
      .filter((t) => t.kind === "topup" && new Date(t.created_at).getTime() > cutoff)
      .reduce((sum, t) => sum + t.amount_cents, 0);
    if (spend >= 8000) return "power";
    if (spend >= 4000) return "pro";
    return "starter";
  }, [recentTx]);

  const dismissNudge = useCallback(async () => {
    if (!user) return;
    const until = new Date(Date.now() + 30 * 86400000).toISOString();
    await supabase.from("user_wallet").update({ nudge_dismissed_until: until }).eq("user_id", user.id);
    refresh();
  }, [user, refresh]);

  return {
    wallet,
    subscription,
    recentTx,
    loading,
    isTrialing,
    hasFunds,
    shouldShowNudge,
    recommendedTier,
    refresh,
    startUsage,
    stopUsage,
    dismissNudge,
  };
}